import LinearLogo from "@/assets/brand-logos/Linear.svg";
import NotionLogo from "@/assets/brand-logos/Notion.svg";
import SentryLogo from "@/assets/brand-logos/Sentry.svg";
import StripeLogo from "@/assets/brand-logos/Stripe.svg";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import {
  useAddMcpServer,
  useRemoveMcpServer,
} from "@/lib/hooks/useMcpServerMutations";
import { useMcpServers } from "@/lib/hooks/useMcpServers";
import { getStatusColor } from "@/lib/utils";
import { usePanelStore } from "@/state/stores";
import {
  Check,
  ChevronLeft,
  Loader2,
  Lock,
  MonitorX,
  MoreHorizontal,
  Plus,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { SerializableMcpConnection } from "../../../../../schemas/codegen";

// Predefined MCP servers
interface PredefinedMcpServer {
  id: string;
  name: string;
  url: string;
}

const PREDEFINED_SERVERS: PredefinedMcpServer[] = [
  {
    id: "notion",
    name: "Notion",
    url: "https://mcp.notion.com/sse", // the /mcp endpoint is not working correctly so suggesting the older version
  },
  {
    id: "linear",
    name: "Linear",
    url: "https://mcp.linear.app/sse",
  },
  {
    id: "sentry",
    name: "Sentry",
    url: "https://mcp.sentry.dev/sse",
  },
  {
    id: "stripe",
    name: "Stripe",
    url: "https://mcp.stripe.com/",
  },
  {
    id: "custom",
    name: "Custom Server",
    url: "",
  },
];

// Get the appropriate logo component for each server
function getServerLogo(serverId: string) {
  switch (serverId) {
    case "notion":
      return NotionLogo;
    case "linear":
      return LinearLogo;
    case "sentry":
      return SentryLogo;
    case "stripe":
      return StripeLogo;
    default:
      return null;
  }
}

// Represents a single server item in the list
interface MCPServerListItemProps {
  server: SerializableMcpConnection;
}

function MCPServerListItem({ server }: MCPServerListItemProps) {
  const { connectionState, url, error, authUrl, name, capabilities, id } =
    server;
  const activeProjectId = useActiveProjectId();
  const removeMcpServer = useRemoveMcpServer();
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const handleDisconnect = async () => {
    if (!activeProjectId) {
      return;
    }

    setIsDisconnecting(true);
    try {
      await removeMcpServer.mutateAsync({
        projectId: activeProjectId,
        serverId: id,
      });
    } catch (error) {
      console.error("Failed to disconnect MCP server:", error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const statusColor = getStatusColor(connectionState);

  return (
    <div className="flex items-center gap-3 rounded-lg bg-muted p-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`h-2 w-2 shrink-0 rounded-full ${statusColor}`} />
        </TooltipTrigger>
        <TooltipContent
          className={
            connectionState === "failed"
              ? "flex items-center gap-2 border-red-900 bg-red-950 text-red-400"
              : ""
          }
        >
          {connectionState === "failed" && error ? (
            <>
              <MonitorX className="h-4 w-4 shrink-0" />
              <p>{error}</p>
            </>
          ) : (
            <p>
              {connectionState.charAt(0).toUpperCase() +
                connectionState.slice(1)}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm text-neutral-300">{name}</div>
        <div className="truncate text-xs text-neutral-500">{url}</div>
        {capabilities && (
          <div className="flex gap-2 mt-1">
            {capabilities.tools && capabilities.tools.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-neutral-600">
                    {capabilities.tools.length} tools
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold text-xs mb-1">
                      Available Tools:
                    </p>
                    <p className="text-xs">{capabilities.tools.join(", ")}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
            {capabilities.resources && capabilities.resources.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-neutral-600">
                    {capabilities.resources.length} resources
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold text-xs mb-1">
                      Available Resources:
                    </p>
                    <p className="text-xs">
                      {capabilities.resources.join(", ")}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      {(connectionState === "authenticating" || connectionState === "failed") &&
        authUrl && (
          <Button
            asChild
            variant="outline"
            className="h-auto shrink-0 px-3 py-1 text-xs border-neutral-700 hover:bg-neutral-800/80"
          >
            <a
              href={
                connectionState === "failed"
                  ? `/api/auth/mcp/restart/${activeProjectId}/${id}`
                  : authUrl
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Authenticate
            </a>
          </Button>
        )}
      <FpDropdownMenu>
        <FpDropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 hover:bg-neutral-700/80"
          >
            <MoreHorizontal className="h-4 w-4 text-neutral-400" />
            <span className="sr-only">MCP Server Options</span>
          </Button>
        </FpDropdownMenuTrigger>
        <FpDropdownMenuContent align="end">
          <FpDropdownMenuItem
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            className="text-red-500 hover:!text-red-500 hover:!bg-red-500/10"
          >
            {isDisconnecting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Disconnecting...
              </>
            ) : (
              "Disconnect"
            )}
          </FpDropdownMenuItem>
        </FpDropdownMenuContent>
      </FpDropdownMenu>
    </div>
  );
}

// Renders the list of available MCP servers
interface MCPServerListProps {
  connections: Record<string, SerializableMcpConnection>;
}

function MCPServerList({ connections }: MCPServerListProps) {
  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-2">
        {Object.values(connections).map((server) => (
          <MCPServerListItem key={server.id} server={server} />
        ))}
      </div>
    </TooltipProvider>
  );
}

// Renders the input and button for adding a new server
function AddMCPServerInput() {
  const activeProjectId = useActiveProjectId();
  const addMcpServer = useAddMcpServer();
  const { isPanelOpen } = usePanelStore();
  const urlInputRef = useRef<HTMLInputElement>(null);
  const [selectedServer, setSelectedServer] =
    useState<PredefinedMcpServer | null>(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState("");
  const [customName, setCustomName] = useState("");
  const [bearerToken, setBearerToken] = useState("");
  const [authPopoverOpen, setAuthPopoverOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCustomServer = selectedServer?.id === "custom";

  // Reset to presets when panel is closed
  useEffect(() => {
    if (!isPanelOpen) {
      setSelectedServer(null);
      setComboboxOpen(false);
      setCustomUrl("");
      setCustomName("");
      setBearerToken("");
      setAuthPopoverOpen(false);
      setError(null);
    }
  }, [isPanelOpen]);

  const isValidUrl = (str: string) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (
    e?: React.FormEvent,
    serverOverride?: PredefinedMcpServer,
  ) => {
    e?.preventDefault();

    if (!activeProjectId) {
      setError("No active project");
      return;
    }

    let url: string;
    let name: string;

    // Use serverOverride if provided (for immediate predefined server addition)
    const serverToUse = serverOverride || selectedServer;

    if (isCustomServer) {
      if (!customUrl.trim()) {
        setError("URL is required");
        return;
      }

      if (!isValidUrl(customUrl.trim())) {
        setError("Please enter a valid URL");
        return;
      }

      url = customUrl.trim();
      name = customName.trim() || new URL(url).hostname;
    } else if (!serverToUse) {
      setError("Please select a server");
      return;
    } else {
      url = serverToUse.url;
      name = serverToUse.name;
    }

    setError(null);
    try {
      await addMcpServer.mutateAsync({
        projectId: activeProjectId,
        url,
        name,
        bearerToken: bearerToken.trim() || undefined,
      });
      // Clear form on success
      setSelectedServer(null);
      setCustomUrl("");
      setCustomName("");
      setBearerToken("");
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to add server");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleBearerTokenKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setAuthPopoverOpen(false);
    }
  };

  return (
    <div className="space-y-2">
      {error && (
        <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2 py-1">
          {error}
        </div>
      )}

      {/* Server Selection or Custom Form */}
      <div className="flex items-center gap-2">
        {isCustomServer ? (
          /* Custom Server Form */
          <>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => {
                setSelectedServer(null);
                setComboboxOpen(true);
              }}
              disabled={addMcpServer.isPending}
              className="h-10 w-10 shrink-0 hover:bg-neutral-700/80"
              aria-label="Back to server selection"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="relative basis-1/2">
              <Input
                ref={urlInputRef}
                type="url"
                name="url"
                aria-label="Server URL"
                placeholder="Server URL (e.g., https://mcp.example.com/sse)"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                className="bg-neutral-800 pr-10"
                disabled={addMcpServer.isPending}
              />
              <Popover open={authPopoverOpen} onOpenChange={setAuthPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-neutral-700/80"
                    type="button"
                  >
                    <Lock className="h-4 w-4 text-neutral-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                        Bearer Authentication
                        {bearerToken && (
                          <Check className="h-4 w-4 text-green-400" />
                        )}
                      </h4>
                      <p className="text-xs text-neutral-500 mb-3">
                        Add a Bearer token for authenticated requests to this
                        MCP server. Note: most servers support authentication
                        over OAuth. Bearer token will take precedence.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Input
                        id="bearer-token"
                        type="password"
                        placeholder="Add your Bearer token..."
                        value={bearerToken}
                        onChange={(e) => setBearerToken(e.target.value)}
                        onKeyDown={handleBearerTokenKeyDown}
                        className="bg-neutral-800"
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              type="text"
              name="name"
              aria-label="Server Name"
              placeholder="Name (optional)"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="bg-neutral-800 text-sm flex-1"
              disabled={addMcpServer.isPending}
            />
            <Button
              variant="ghost"
              type="button"
              onClick={handleSubmit}
              disabled={addMcpServer.isPending || !customUrl.trim()}
              className="hover:bg-neutral-700/80"
            >
              {addMcpServer.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </>
              )}
            </Button>
          </>
        ) : (
          /* Predefined Server Selection */
          <>
            <Combobox
              items={PREDEFINED_SERVERS}
              value={selectedServer}
              open={comboboxOpen}
              onOpenChange={setComboboxOpen}
              onChange={(server) => {
                setSelectedServer(server);
                // Handle different server types
                if (server.id === "custom") {
                  // Focus URL input when custom server is selected
                  setTimeout(() => urlInputRef.current?.focus(), 0);
                } else {
                  // Immediately add predefined servers
                  handleSubmit(undefined, server);
                }
              }}
              getItemKey={(item) => item.id}
              getSearchableText={(item) => item.name}
              renderTrigger={(item) => {
                if (!item) {
                  return (
                    <span className="text-sm text-neutral-500">
                      Select an MCP server...
                    </span>
                  );
                }
                const LogoComponent = getServerLogo(item.id);
                return (
                  <div className="flex items-center gap-2">
                    {LogoComponent && (
                      <LogoComponent className="h-4 w-4 shrink-0" />
                    )}
                    <span className="text-sm text-neutral-300">
                      {item.name}
                    </span>
                  </div>
                );
              }}
              renderItem={(item, isSelected) => {
                const LogoComponent = getServerLogo(item.id);
                return (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      {LogoComponent && (
                        <LogoComponent className="h-4 w-4 shrink-0" />
                      )}
                      <div>
                        <div className="text-sm text-neutral-300">
                          {item.name}
                        </div>
                        {item.url && (
                          <div className="text-xs text-neutral-500">
                            {item.url}
                          </div>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="h-4 w-4 text-neutral-400" />
                    )}
                  </div>
                );
              }}
              placeholder="Search servers..."
              emptyMessage="No servers found."
              className="bg-muted border-focus hover:bg-background w-full"
            />
          </>
        )}
      </div>
    </div>
  );
}

export function McpServersTab() {
  const activeProjectId = useActiveProjectId();
  const { data: connections = {}, isLoading } = useMcpServers(activeProjectId);

  return (
    <main className="grid gap-3">
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full bg-neutral-800" />
          <Skeleton className="h-16 w-full bg-neutral-800" />
          <Skeleton className="h-16 w-full bg-neutral-800" />
        </div>
      ) : Object.keys(connections as object).length === 0 ? (
        <div className="py-4 text-center text-muted-foreground">
          <p className="text-sm">No MCP servers connected</p>
        </div>
      ) : (
        <MCPServerList
          connections={connections as Record<string, SerializableMcpConnection>}
        />
      )}
      <AddMCPServerInput />
    </main>
  );
}
